import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useRecoilValue } from "recoil";
import { t } from "i18next";
import { toast } from "react-toastify";
import { getAISettingInitial, getMyModuleServer } from "common-lib";
import { DefectOptionItem, InitialOptionValue } from "@types";
import { myAIModuleState, trainUuidState } from "../recoil/upgrade";
import { editProjectConfig } from "../services/projectService";
import { fetchModuleServer } from "../services/aiServer";
import { descType, useAIModuleSync } from "./useAIModuleSync";
import {
  addNewOptionToJson,
  chainOrderWarnings,
  extractAugmentationOptions,
  optionsToJson,
  parseOptionsJson,
  restoreOptionToJson,
  wireChain,
  withoutConnection,
} from "../utils/defectUtils";

// 같은 옵션인지 비교용 (tag/connection은 사용자 편집 대상이 아니라 제외. 순서는 connection이 되므로 비교에 포함)
const augSignature = (options: Record<string, DefectOptionItem>) =>
  JSON.stringify(Object.entries(options).map(([key, { name, para }]) => [key, name, withoutConnection(para)]));

// augmentation 옵션 편집 상태 (SIMPLE/JSON 편집, Save=/sync).
// DefectPreview가 하나만 만들고, 옵션 설정 칸과 이미지 풀스크린의 옵션 패널(AugOptionSection 두 개)이 같이 쓴다
export const useAugOptions = () => {
  const { id } = useParams();
  const myAIModule = useRecoilValue(myAIModuleState);
  const trainUuid = useRecoilValue(trainUuidState);
  const syncModule = useAIModuleSync();

  const [editedAugOptions, setEditedAugOptions] = useState<Record<string, DefectOptionItem>>({});
  const [savedAugOptions, setSavedAugOptions] = useState<Record<string, DefectOptionItem>>({}); // AI 서버에 저장된 값
  const [initialOptions, setInitialOptions] = useState<Record<string, InitialOptionValue>>({});
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  // augmentation이 아닌 모듈 키(model1 등): JSON에서 이 키로 옵션을 만들면 그 모듈을 덮어쓰므로 막는다
  const [reservedKeys, setReservedKeys] = useState<string[]>([]);

  // JSON 모드: 들어갈 때의 옵션(jsonBase) 기준으로 편집 텍스트를 매번 다시 해석한다
  const [jsonBase, setJsonBase] = useState<Record<string, DefectOptionItem> | null>(null);
  const [jsonText, setJsonText] = useState("");
  const [jsonError, setJsonError] = useState<string | null>(null);
  const [jsonPretty, setJsonPretty] = useState(false);

  // on/off 변경 직전 상태들 (뒤로 버튼용. 저장과 무관하게 편집 중 상태만 되돌린다)
  const [undoStack, setUndoStack] = useState<Record<string, DefectOptionItem>[]>([]);
  const optionsRef = useRef(editedAugOptions); // 콜백 참조를 고정하려고(=memo된 패널 유지) state 대신 ref로 읽는다
  optionsRef.current = editedAugOptions;
  const pushUndo = useCallback(() => setUndoStack((s) => [...s.slice(-19), optionsRef.current]), []);

  const dirty = useMemo(
    () => augSignature(editedAugOptions) !== augSignature(savedAugOptions),
    [editedAugOptions, savedAugOptions],
  );

  // 참조가 고정돼야 memo된 DefectOptionPanel이 렌더마다 다시 그려지지 않는다
  const handleOptionChange = useCallback(
    (optionKey: string, paramKey: string, value: any) => {
      if (paramKey === "mode") pushUndo(); // ponytail: 되돌리기는 on/off만 (값 입력은 타이핑마다 쌓여 쓸모없다)
      setEditedAugOptions((prev) => ({
        ...prev,
        [optionKey]: { ...prev[optionKey], para: { ...prev[optionKey].para, [paramKey]: value } },
      }));
    },
    [pushUndo],
  );

  // SIMPLE 드래그로 순서 변경. 키 순서 = 파이프라인 순서라 Save 때 wireChain이 connection을 다시 계산한다
  const handleReorder = useCallback(
    (from: number, to: number) =>
      setEditedAugOptions((prev) => {
        const entries = Object.entries(prev);
        const [moved] = entries.splice(from, 1);
        entries.splice(to, 0, moved);
        return Object.fromEntries(entries);
      }),
    [],
  );

  // 전체 on/off: mode가 enabled/disabled인 옵션만 대상 (mode가 없는 옵션은 그대로)
  const canToggle = (o: DefectOptionItem) => o.para?.mode === "enabled" || o.para?.mode === "disabled";
  const togglable = Object.values(editedAugOptions).filter(canToggle);
  const allOn = togglable.length > 0 && togglable.every((o) => o.para.mode === "enabled");

  // 편집 중 옵션 교체 + CODE 모드면 JSON도 다시 그린다 (disabled는 JSON에서 숨김 → 다시 켜면 다시 보임)
  const showOptions = (next: Record<string, DefectOptionItem>) => {
    setEditedAugOptions(next);
    if (jsonBase) {
      setJsonText(optionsToJson(next, jsonPretty));
      setJsonError(null);
    }
  };

  // 바뀐 게 있을 때만 되돌리기 스택에 쌓고 반영 (같은 옵션을 또 solo 해도 쌓이지 않게)
  const applyOptions = (next: Record<string, DefectOptionItem>) => {
    if (augSignature(next) === augSignature(optionsRef.current)) return;
    pushUndo();
    showOptions(next);
  };

  const setAllModes = (on: boolean) => {
    const mode = on ? "enabled" : "disabled";
    applyOptions(
      Object.fromEntries(
        Object.entries(editedAugOptions).map(([key, o]) => [key, canToggle(o) ? { ...o, para: { ...o.para, mode } } : o]),
      ),
    );
  };

  // 옵션 하나만 켜고 나머지는 끈다 (SIMPLE 패널에서 우클릭)
  const soloOption = useCallback(
    (optionKey: string) => {
      applyOptions(
        Object.fromEntries(
          Object.entries(optionsRef.current).map(([key, o]) => [
            key,
            canToggle(o) ? { ...o, para: { ...o.para, mode: key === optionKey ? "enabled" : "disabled" } } : o,
          ]),
        ),
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pushUndo, jsonBase, jsonPretty],
  );

  // 뒤로: 직전 on/off 상태로 되돌린다 (저장이 아니라 편집 중 상태만)
  const undo = () => {
    const prev = undoStack[undoStack.length - 1];
    if (!prev) return;
    setUndoStack((s) => s.slice(0, -1));
    showOptions(prev);
  };

  const fetchOptions = async () => {
    setOptionsLoading(true);
    try {
      const [response, initialOpts] = await Promise.all([
        fetchModuleServer(id),
        getAISettingInitial(myAIModule!.serverAddress),
      ]);
      const opts = response.module.options;
      const augOptions = extractAugmentationOptions(opts);
      setEditedAugOptions(augOptions);
      setUndoStack([]);
      setSavedAugOptions(augOptions);
      setReservedKeys(Object.keys(opts).filter((key) => !(key in augOptions)));
      setInitialOptions(initialOpts);
      return augOptions;
    } finally {
      setOptionsLoading(false);
    }
  };

  // AI 서버의 현재 옵션을 다시 받아 SIMPLE/JSON에 보여준다. 모드는 그대로 유지
  const reloadOptions = async () => {
    try {
      const aug = await fetchOptions();
      if (jsonBase) {
        setJsonBase(aug);
        setJsonText(optionsToJson(aug, jsonPretty));
        setJsonError(null);
      }
    } catch (error) {
      console.error(error);
      toast.error(t("OptionLoadFailMsg", "옵션을 불러오지 못했습니다."));
    }
  };

  useEffect(() => {
    if (myAIModule) fetchOptions().catch(console.error);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleJsonChange = (text: string) => {
    setJsonText(text);
    try {
      setEditedAugOptions(parseOptionsJson(text, jsonBase ?? {}, reservedKeys));
      setJsonError(null);
    } catch (e: any) {
      setJsonError(e.message);
    }
  };

  const toggleJsonMode = () => {
    if (jsonBase) {
      if (jsonError) return; // 틀린 JSON은 SIMPLE로 못 돌아간다
      setJsonBase(null);
    } else {
      setJsonBase(editedAugOptions);
      setJsonText(optionsToJson(editedAugOptions, jsonPretty));
      setJsonError(null);
    }
  };

  // 편집 중인 텍스트 기준으로 다시 포맷 (지운 키가 되살아나지 않게)
  const handleJsonPretty = (pretty: boolean) => {
    setJsonPretty(pretty);
    if (!jsonError) setJsonText(optionsToJson(JSON.parse(jsonText), pretty));
  };

  // JSON에서 지웠거나 disabled라 숨겨진 옵션을 원래 name/para로 다시 넣는다
  const restoreJsonOption = (key: string) =>
    handleJsonChange(restoreOptionToJson(jsonText, jsonBase ?? {}, key, jsonPretty));

  // 새 옵션 추가: 정해진 모듈 이름 중 하나를 고르면 AI 초기설정 기본 para로 한 줄 추가
  const addNewOption = (name: string) => {
    const taken = [...Object.keys(jsonBase ?? {}), ...reservedKeys];
    handleJsonChange(addNewOptionToJson(jsonText, name, initialOptions[name]?.init, taken, jsonPretty));
  };

  // Save: 편집한 옵션을 /sync로 AI 서버에 반영. 성공 여부를 돌려준다 (이미지 새로고침이 저장 후 이어서 진행)
  // augModes도 이때 저장 → 백엔드가 학습 시작/학습·검증 완료 시 이 on/off를 따른다
  const save = async (): Promise<boolean> => {
    if (!myAIModule || !trainUuid) {
      toast.error(t("NoAIModuleMsg", "연결된 AI 모듈이 없습니다."));
      return false;
    }
    if (jsonBase && jsonError) {
      toast.error(t("OptionJsonErrorMsg", "옵션 JSON 형식을 먼저 고쳐주세요."));
      return false;
    }
    setSaving(true);
    try {
      const augModes = Object.fromEntries(Object.entries(editedAugOptions).map(([key, o]) => [key, o.para?.mode]));
      if (Object.keys(augModes).length) await editProjectConfig(Number(id), { augModes }); // 옵션을 못 불러왔으면 저장값 유지
      const before = (await getMyModuleServer(id, null)).module; // /sync 직전이라 캐시 없이 새로 받는다
      const wired = wireChain(editedAugOptions, before.options); // 옵션 순서대로 connection 다시 계산
      await syncModule({ ...before.options, ...wired }, descType(before.desc), before.desc);
      toast.success(t("ModuleUpdateSuccessMsg"));
      chainOrderWarnings(editedAugOptions).forEach((msg) => toast.warning(msg)); // 권장 순서를 벗어나도 저장은 하되 알려준다
      await reloadOptions(); // 저장된 서버 값 기준으로 다시 표시 (JSON 숨김/비활성 목록도 갱신)
      return true;
    } catch (error: any) {
      console.error(error);
      toast.error(error?.response?.data?.error ?? error?.message ?? t("AIModuleUpdateFailedMsg"));
      return false;
    } finally {
      setSaving(false);
    }
  };

  return {
    editedAugOptions,
    initialOptions,
    optionsLoading,
    saving,
    dirty,
    allOn,
    canToggleAll: togglable.length > 0,
    setAllModes,
    soloOption,
    undo,
    canUndo: undoStack.length > 0,
    jsonBase,
    jsonText,
    jsonError,
    jsonPretty,
    handleOptionChange,
    handleReorder,
    reloadOptions,
    handleJsonChange,
    toggleJsonMode,
    handleJsonPretty,
    restoreJsonOption,
    addNewOption,
    save,
  };
};

export type AugOptionsController = ReturnType<typeof useAugOptions>;
